import { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Trophy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";

const WORD_RE = /([A-Za-z']+)/g;

function grade(target, guess) {
  const t = (target.toLowerCase().match(WORD_RE) || []);
  const g = (guess.toLowerCase().match(WORD_RE) || []);
  if (t.length === 0) return { accuracy: 1, missed: [] };
  const counts = {};
  t.forEach((w) => (counts[w] = (counts[w] || 0) + 1));
  let hits = 0;
  g.forEach((w) => { if (counts[w] > 0) { hits++; counts[w]--; } });
  const missed = Object.entries(counts).filter(([, c]) => c > 0).map(([w]) => w);
  return { accuracy: hits / t.length, missed };
}

/**
 * Test Mode — sequential graded recall of every section in a song.
 * Rules: no reveals, no AI Coach, no do-overs mid-test. One final score at the end
 * that gets recorded against the song via /api/songs/:id/practice with mode='test'.
 */
export default function TestMode({ songId, sections, onFinish }) {
  const realSections = useMemo(
    () => (sections || []).filter((s) => s.name !== "Whole song"),
    [sections]
  );
  const [idx, setIdx] = useState(0);
  const [attempt, setAttempt] = useState("");
  const [results, setResults] = useState([]); // {section, accuracy, missed}
  const [state, setState] = useState("idle"); // idle | running | grading | done
  const [finalAcc, setFinalAcc] = useState(0);

  const start = () => {
    setIdx(0); setAttempt(""); setResults([]); setState("running"); setFinalAcc(0);
  };

  const submitSection = () => {
    const s = realSections[idx];
    const r = grade(s.text, attempt);
    const next = [...results, { section: s.name, accuracy: r.accuracy, missed: r.missed }];
    setResults(next);
    setAttempt("");
    if (idx + 1 >= realSections.length) {
      finish(next);
    } else {
      setIdx(idx + 1);
    }
  };

  const finish = async (finalResults) => {
    setState("grading");
    const total = finalResults.length ? finalResults.reduce((a, r) => a + r.accuracy, 0) / finalResults.length : 0;
    setFinalAcc(total);
    const missedAll = finalResults.flatMap((r) => r.missed || []);
    try {
      const { data } = await api.post(`/songs/${songId}/practice`, {
        mode: "test",
        accuracy: total,
        section_name: "Full test",
        combo: 0,
        missed_words: missedAll,
      });
      toast.success(`Test complete · +${data.xp_gain} XP · Mastery ${Math.round(data.mastery)}%`);
      if (total >= 0.9) celebrate("big");
      if (data.unlocked?.length) data.unlocked.forEach((a) => toast(`Unlocked: ${a.title}`, { description: a.desc }));
      onFinish?.(total);
    } catch (e) { toast.error(formatApiError(e)); }
    setState("done");
  };

  const restart = () => start();

  if (realSections.length === 0) {
    return (
      <div data-testid="test-mode-empty" className="p-6 text-muted-foreground">
        This song doesn't have sections yet. Add sections in the lyric editor to run a test.
      </div>
    );
  }

  if (state === "idle") {
    return (
      <div data-testid="test-mode-intro" className="text-center py-10">
        <p className="overline-label text-accent">Test yourself</p>
        <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
          Type every section from memory.<br />No hints. One score.
        </h3>
        <p className="mt-4 text-muted-foreground max-w-md mx-auto">
          You'll be prompted through <span className="font-mono text-foreground">{realSections.length}</span> sections in order.
          Nothing is revealed until you finish. Ready?
        </p>
        <Button onClick={start} className="mt-8 rounded-full h-12 px-8 glow-amber-sm hover:glow-amber transition-shadow"
                data-testid="test-mode-start-btn">
          Begin the test <ArrowRight size={16} className="ml-1.5" />
        </Button>
      </div>
    );
  }

  if (state === "running") {
    const s = realSections[idx];
    const progress = Math.round((idx / realSections.length) * 100);
    return (
      <div data-testid="test-mode-running">
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="overline-label">
            Section {idx + 1} of {realSections.length}
          </span>
          <span className="font-mono text-xs text-muted-foreground">{progress}%</span>
        </div>
        <div className="h-1 rounded-full bg-secondary overflow-hidden mb-6">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <p className="overline-label mb-2 text-accent" data-testid="test-mode-section-name">{s.name}</p>
        <p className="text-sm text-muted-foreground mb-4">
          Type this section from memory. Order matters less than getting the words right.
        </p>
        <textarea
          data-testid="test-mode-input"
          value={attempt}
          onChange={(e) => setAttempt(e.target.value)}
          placeholder={`Type ${s.name.toLowerCase()} from memory…`}
          className="w-full min-h-[220px] p-5 rounded-2xl border border-border bg-card lyric-line text-xl leading-relaxed outline-none focus:ring-2 focus:ring-primary transition-shadow"
        />
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-muted-foreground">Nothing is revealed until the final score.</span>
          <Button onClick={submitSection} className="rounded-full h-11 px-6" data-testid="test-mode-submit-btn">
            <Check size={16} className="mr-1.5" />
            {idx + 1 >= realSections.length ? "Finish test" : "Next section"}
          </Button>
        </div>
      </div>
    );
  }

  if (state === "grading") {
    return <div className="p-10 text-center text-muted-foreground" data-testid="test-mode-grading">Grading…</div>;
  }

  // done
  const finalPct = Math.round(finalAcc * 100);
  const finalMessage =
    finalAcc >= 0.9 ? "Locked in. Take the stage."
      : finalAcc >= 0.7 ? "Almost there — one more Play round and try again."
      : "This song still needs some Play time before showtime.";
  const rowClass = (acc) =>
    acc >= 0.9 ? "text-primary" : acc >= 0.5 ? "text-accent" : "text-destructive";

  return (
    <div data-testid="test-mode-done" className="py-6">
      <div className="text-center">
        <p className="overline-label text-accent">Test result</p>
        <div className="mt-3 inline-flex items-baseline gap-2">
          <span className="font-display text-7xl md:text-8xl text-primary" data-testid="test-mode-final-acc">
            {finalPct}
          </span>
          <span className="font-mono text-2xl text-muted-foreground">%</span>
        </div>
        <p className="mt-2 text-muted-foreground">{finalMessage}</p>
      </div>

      <ul className="mt-8 divide-y divide-border border-y border-border">
        {results.map((r, i) => (
          <li key={`${r.section}-${i}`} className="py-3 flex items-center justify-between gap-4">
            <span className="overline-label">{r.section}</span>
            <span className={`font-mono ${rowClass(r.accuracy)}`}>
              {Math.round(r.accuracy * 100)}%
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex items-center justify-center gap-3">
        <Button onClick={restart} variant="outline" className="rounded-full h-11 px-5"
                data-testid="test-mode-retry-btn">
          <RotateCcw size={16} className="mr-1.5" /> Test again
        </Button>
        {finalAcc >= 0.85 && (
          <Button onClick={() => onFinish?.(finalAcc, "perform")} className="rounded-full h-11 px-5"
                  data-testid="test-mode-perform-btn">
            <Trophy size={16} className="mr-1.5" /> Try Perform mode
          </Button>
        )}
      </div>
    </div>
  );
}
